import { axesView, composeHeading, createStoreMethodologyFilter, defineDashboard, isKEur, resolveProduct } from '../../../_shared/dashboard-axes';
import { createKpiFilters } from '../../../../dashboard-config/kpis/kpi-filters';
import { CreateDashboardDefinitionBase } from '../../../_shared/dashboard-definition.model';
import {
  SortAlphabeticallyByColumnDataTransformer,
  SortByYearDataTransformer,
  UndefinedStateToSumGermanyDashboardDataTransformer,
} from '../../../../data_transformers/dashboard_data_transformer';
import {
  createIdNameTransformers,
  granularityHeadingPhrase,
  groupDetailOf,
  mapHeadingPhrase,
} from '../../../../dashboard-config/granularities/granularity-profile';
import {
  DashboardChartTypeSingleSelectFilter,
  DashboardForecastFromActivatedRouteSingleSelectFilter,
} from '../../../../filters/dashboard_filter_implementations';
import { createTimeFilters, resolveTimeFilter } from '../../../_shared/time-filters';
import {
  createForecastChartTransformers,
  createForecastValueChangeCiTransformers,
  FORECAST_METHODOLOGY_IDS,
  ForecastComparisonMetricProfile,
} from '../forecast-metric.profile';
import { DashboardForecastComparisonDefinition } from './dashboard-forecast-comparison.definition';
import {
  DashboardForecastComparisonShell,
  dashboardForecastComparisonShellProfiles,
} from './dashboard-forecast-comparison-shell.profile';

export type CreateDashboardForecastComparisonDefinitionInput =
  CreateDashboardDefinitionBase<
    DashboardForecastComparisonShell,
    ForecastComparisonMetricProfile
  >;

export function createDashboardForecastComparisonDefinition(
  input: CreateDashboardForecastComparisonDefinitionInput,
): DashboardForecastComparisonDefinition {
  const shellKey = input.shell;
  const { axes, shell, metric } = resolveProduct(input, dashboardForecastComparisonShellProfiles);
  const granularityId = axes.granularity.id;
  const keys = metric.keys;
  // Forecast datasets are yearly, also for Employed.
  const time = resolveTimeFilter(axes, 'range', { yearly: true, defaultYearRange: 9 });

  return defineDashboard<DashboardForecastComparisonDefinition>(axes, shell, {
    heading: (filterValues) =>
      composeHeading(
        [
          'PROGNOSE DER',
          metric.titleMetric(filterValues),
          shell.chartHeadingGranularity?.(filterValues) ?? granularityHeadingPhrase(granularityId, filterValues),
        ],
        filterValues.get('years'),
      ),
    mapHeading: (filterValues) =>
      composeHeading([metric.mapHeadingMetricLabel(filterValues), mapHeadingPhrase(granularityId, filterValues)]),

    // Order: years, granularity, KPI, methodology, chart type, forecast.
    filters: (deps) => [
      ...createTimeFilters(deps, time),
      ...shell.buildGranularityFilters(deps),
      ...createKpiFilters(axes.metric, granularityId),
      createStoreMethodologyFilter(deps, { methodologies: FORECAST_METHODOLOGY_IDS, width: '210px' }),
      new DashboardChartTypeSingleSelectFilter(deps.localDatasetsService, { defaultValue: 'line' }),
      new DashboardForecastFromActivatedRouteSingleSelectFilter(deps.router, deps.localDatasetsService, {
        showInFilterComponent: false,
      }),
    ],

    preFilterTransformers: () => [new UndefinedStateToSumGermanyDashboardDataTransformer()],

    postFilterTransformers: (deps) => [
      ...createForecastValueChangeCiTransformers(keys, granularityId),
      new SortByYearDataTransformer(),
      // Bundesvergleich WZ-Klassen rows are states × WZ classes.
      ...createIdNameTransformers(deps, shell.datasetGranularityId ?? granularityId),
      ...createForecastChartTransformers(metric, granularityId),
      new SortAlphabeticallyByColumnDataTransformer(shell.sortingKey),
    ],

    exportTransformers: (_deps) => metric.createExportTransformers(),

    view: {
      valueKey: keys.valueKey,
      valueChangeKey: keys.valueChangeKey,
      valueUpperKey: keys.valueUpperKey,
      valueLowerKey: keys.valueLowerKey,
      shellKey,
      ...axesView(axes),
      groupDetail: groupDetailOf(granularityId, 'code'),
      kEur: isKEur(axes),
      enablePeerStateDefault: shell.enablePeerStateDefault,
    },
  });
}
